import React, { useEffect, useState } from "react";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Link, useNavigate } from "react-router-dom";
import client, { gbp, resolveImageUrl } from "@/lib/api";
import PromoCodeInput from "@/components/PromoCodeInput";
import { useMaintenance } from "@/context/MaintenanceContext";

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, subtotal, promoCode } = useCart();
  const { enabled: maintenanceOn } = useMaintenance();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!open || items.length === 0) { setQuote(null); return; }
    const payload = items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color || null, size_fit: i.size_fit || null }));
    client.post("/cart/quote", { items: payload, promo_code: promoCode || null })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [open, items, promoCode]);

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setOpen(false)}
        data-testid="cart-overlay"
      />
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[440px] bg-white border-l border-black flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between p-6 border-b border-black/10">
          <div className="font-display text-xl tracking-tight">Your Cart</div>
          <button onClick={() => setOpen(false)} data-testid="cart-close-btn"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {items.length === 0 ? (
            <div className="p-12 text-center text-black/50 text-sm">Your cart is empty.</div>
          ) : (
            items.map((it) => (
              <div key={`${it.product_id}-${it.size}-${it.color || ""}`} className="flex gap-4 p-6 border-b border-black/10" data-testid={`cart-item-${it.product_id}`}>
                <div className="w-20 h-24 bg-[#FAFAFA] overflow-hidden">
                  <img src={resolveImageUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg">{it.title}</div>
                  <div className="label-eyebrow text-black/40 mt-1">Size {it.size}{it.size_fit ? ` · ${it.size_fit === "men" ? "Mens" : "Womens"}` : ""}{it.color ? ` · ${it.color}` : ""}</div>
                  <div className="mt-2 text-sm">{gbp(it.unit)}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <button onClick={() => updateQty(it.product_id, it.size, it.quantity - 1, it.color)} className="hairline-strong w-7 h-7 flex items-center justify-center" data-testid={`qty-dec-${it.product_id}`}><Minus size={12} /></button>
                    <span className="text-sm w-6 text-center">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.size, it.quantity + 1, it.color)} className="hairline-strong w-7 h-7 flex items-center justify-center" data-testid={`qty-inc-${it.product_id}`}><Plus size={12} /></button>
                    <button onClick={() => remove(it.product_id, it.size, it.color)} className="ml-auto label-eyebrow text-black/40 hover:moto-accent" data-testid={`cart-remove-${it.product_id}`}>Remove</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="border-t border-black p-6 space-y-4">
          {items.length > 0 && (
            <>
              <PromoCodeInput compact />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="label-eyebrow">Subtotal</span>
                  <span>{gbp(quote?.subtotal ?? subtotal)}</span>
                </div>
                {Number(quote?.bundle_savings || 0) > 0 && (
                  <div className="flex justify-between moto-accent">
                    <span className="label-eyebrow">Bundle savings</span>
                    <span>- {gbp(quote.bundle_savings)}</span>
                  </div>
                )}
                {quote?.bulk_pct > 0 && (
                  <div className="flex justify-between moto-accent">
                    <span className="label-eyebrow">Bulk {quote.bulk_pct}%</span>
                    <span>- {gbp(quote.discount_amt)}</span>
                  </div>
                )}
                {quote?.promo && (
                  <div className="flex justify-between moto-accent">
                    <span className="label-eyebrow">Promo {quote.promo.code}</span>
                    <span>- {gbp(quote.promo.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="label-eyebrow">Shipping</span>
                  <span>{Number(quote?.shipping || 0) > 0 ? gbp(quote.shipping) : <span className="moto-accent">Free</span>}</span>
                </div>
                {Number(quote?.free_shipping_remaining || 0) > 0 && (
                  <div className="text-[11px] text-black/40 -mt-1">Add {gbp(quote.free_shipping_remaining)} for free UK shipping</div>
                )}
                {Number(quote?.free_shipping_remaining || 0) > 0 && Number(quote.free_shipping_remaining) <= 10 && (
                  <div className="border border-[#FF3B30] bg-[#FFF5F4] p-3 mt-1" data-testid="cart-nudge">
                    <div className="label-eyebrow moto-accent">Almost there</div>
                    <div className="text-xs text-black/70 mt-1">
                      You're <span className="font-medium">{gbp(quote.free_shipping_remaining)}</span> from <strong>free UK shipping</strong>. Add one more piece and we'll waive the £4.99 postage.
                    </div>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-black/20 font-medium">
                  <span>Total</span>
                  <span>{gbp(quote?.total ?? subtotal)}</span>
                </div>
              </div>
            </>
          )}
          <button
            disabled={items.length === 0 || maintenanceOn}
            onClick={() => { if (maintenanceOn) return; setOpen(false); navigate("/checkout"); }}
            className="w-full bg-moto-gradient text-white py-4 label-eyebrow disabled:opacity-40 hover:bg-black hover:[background-image:none] transition-colors shadow-[0_6px_16px_rgba(255,59,48,0.35)]"
            data-testid="cart-checkout-btn"
          >
            {maintenanceOn ? "Checkout paused" : "Checkout"}
          </button>
          {maintenanceOn && (
            <p className="text-xs text-center text-black/60 mt-2" data-testid="cart-drawer-maintenance-note">
              MotoWear is in browse-only mode — checkout reopens shortly.
            </p>
          )}
          <Link to="/shop" onClick={() => setOpen(false)} className="block text-center label-eyebrow text-black/60">Continue shopping</Link>
        </div>
      </aside>
    </>
  );
}
